import { useState } from "react";
import { Download, ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { submitLead } from "@/lib/leads";

interface LeadMagnetProps {
  title: string;
  description: string;
  buttonText?: string;
}

const LeadMagnet = ({ title, description, buttonText = "Download Free Guide" }: LeadMagnetProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [_honey, setHoney] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (_honey) return;
    if (!email.trim() || !name.trim()) return;
    await submitLead({ form_type: "lead_magnet", name, email, metadata: { guide: title } });
    setSubmitted(true);
  };

  if (!open) {
    return (
      <div className="relative overflow-hidden rounded-2xl p-8 text-center border border-border shadow-card" style={{
        background: `linear-gradient(135deg, hsl(270 60% 95%) 0%, hsl(210 40% 98%) 40%, hsl(270 50% 93%) 70%, hsl(210 40% 98%) 100%)`
      }}>
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-20 blur-3xl" style={{
          background: `radial-gradient(circle, hsl(270 70% 55%), transparent 70%)`
        }} />
        <Download className="w-10 h-10 text-primary mx-auto mb-4 relative" />
        <h3 className="text-xl font-semibold text-foreground mb-2 relative">{title}</h3>
        <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto relative">{description}</p>
        <Button onClick={() => setOpen(true)} size="lg" className="relative">
          {buttonText} <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="relative overflow-hidden rounded-2xl p-8 text-center border border-border shadow-card" style={{
        background: `linear-gradient(135deg, hsl(270 60% 95%) 0%, hsl(210 40% 98%) 40%, hsl(270 50% 93%) 70%, hsl(210 40% 98%) 100%)`
      }}>
        <Download className="w-10 h-10 text-primary mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-foreground mb-2">Check Your Email!</h3>
        <p className="text-muted-foreground text-sm">We've sent the guide to {email}. Check your inbox shortly.</p>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl p-8 border border-border shadow-card" style={{
        background: `linear-gradient(135deg, hsl(270 60% 95%) 0%, hsl(210 40% 98%) 40%, hsl(270 50% 93%) 70%, hsl(210 40% 98%) 100%)`
      }}>
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-15 blur-3xl" style={{
          background: `radial-gradient(circle, hsl(270 70% 55%), transparent 70%)`
        }} />
      <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
        <X className="w-5 h-5" />
      </button>
      <h3 className="text-xl font-semibold text-foreground mb-2 relative">{title}</h3>
      <p className="text-muted-foreground text-sm mb-6 relative">{description}</p>
      <form onSubmit={handleSubmit} className="space-y-3 max-w-sm mx-auto relative">
        <input type="text" name="_honey" value={_honey} onChange={(e) => setHoney(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <input
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full h-11 px-4 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          required
          maxLength={100}
        />
        <input
          type="email"
          placeholder="Your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full h-11 px-4 rounded-xl bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          required
          maxLength={255}
        />
        <Button type="submit" className="w-full h-11">
          Get Free Access <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
        <p className="text-xs text-muted-foreground text-center">No spam. We never sell your information. Unsubscribe anytime.</p>
      </form>
    </div>
  );
};

export default LeadMagnet;
